import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useContent } from "../context/ContentContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/accommodation", label: "Accommodation" },
  { to: "/facilities", label: "Facilities" },
  { to: "/gallery", label: "Gallery" },
  { to: "/experiences", label: "Experiences" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { content } = useContent();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled || location.pathname !== "/";

  return (
    <header
      data-testid="navbar"
      style={{ top: "var(--promo-h, 0px)" }}
      className={`fixed inset-x-0 z-50 transition-colors duration-300 ${
        dark ? "bg-forest/60 backdrop-blur-md border-b border-white/10" : "bg-gradient-to-b from-black/40 to-transparent"
      }`}
    >
      <nav className="container-lux flex items-center justify-between h-20 md:h-24 relative">
        <div className="hidden lg:flex flex-1 items-center gap-5">
          {LINKS.slice(0, 5).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                location.pathname === l.to ? "text-gold" : "text-cream/85 hover:text-gold"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 z-10 absolute left-1/2 -translate-x-1/2 lg:static lg:left-auto lg:translate-x-0">
          {content?.logo_url ? (
            <img src={content.logo_url} alt="The Rose Air BnB" className="h-14 md:h-16 w-auto object-contain" />
          ) : (
            <span className="font-serif text-xl md:text-2xl text-cream tracking-wide">The Rose Air <span className="text-gold">BnB</span></span>
          )}
        </Link>

        <div className="hidden lg:flex flex-1 items-center justify-end gap-5">
          {LINKS.slice(5).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                location.pathname === l.to ? "text-gold" : "text-cream/85 hover:text-gold"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button data-testid="nav-book-now-button" onClick={() => navigate("/booking")} className="btn-primary py-2.5 px-6 whitespace-nowrap">
            Book Now
          </button>
          <Link
            to="/manage"
            data-testid="nav-link-manage"
            className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap ${
              location.pathname.startsWith("/manage") ? "text-gold" : "text-cream/85 hover:text-gold"
            }`}
          >
            Manage Booking
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-cream p-2 ml-auto"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-forest border-t border-white/10 px-5 pb-6 pt-2">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="block py-3 text-cream/90 border-b border-white/5 text-sm">
              {l.label}
            </Link>
          ))}
          <Link to="/manage" data-testid="mobile-nav-manage" className="block py-3 text-cream/90 border-b border-white/5 text-sm">Manage Booking</Link>
          <button onClick={() => navigate("/booking")} className="btn-primary w-full mt-4">Book Now</button>
        </div>
      )}
    </header>
  );
}
